import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import {
  DataGrid,
  gridClasses,
  GridColDef,
  GridPaginationModel,
} from "@mui/x-data-grid";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { CompanyLogo } from "../../../../../../components/CompanyLogo";
import { formatNumber } from "../../../../../../utils/numberUtils";
import { LightTooltip } from "../../../../../../components/LightTooltip";
import { EmptyState } from "../../../../components/EmptyState";
import { lightScrollbarStyle } from "../../../../../../styles/lightScrollbarStyle";

export interface IntensityData {
  productName: string;
  value: number;
  vendorUrl: string;
}

interface IntensityTableProps {
  data: IntensityData[];
  loading: boolean;
  title: string;
  helpText: string;
  highlightText: string;
  count: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
}

export const IntensityTable: React.FC<IntensityTableProps> = ({
  data,
  loading,
  title,
  helpText,
  highlightText,
  count,
  paginationModel,
  onPaginationModelChange,
}) => {
  const columns: GridColDef<IntensityData>[] = [
    {
      field: "productName",
      headerName: "Product",
      flex: 1,
      minWidth: 140,
      resizable: false,
      renderCell: (params) => {
        if (loading) {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                width: "100%",
              }}
            >
              <Skeleton variant="rounded" width={24} height={24} />
              <Skeleton variant="text" sx={{ flexGrow: 1 }} />
            </Box>
          );
        }

        return (
          <LightTooltip title={params.value} placement="bottom-start">
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              overflow="hidden"
              marginLeft={1}
            >
              <CompanyLogo
                domain={params.row.vendorUrl}
                size={24}
                variant="rounded"
              />
              <Typography variant="body2" noWrap>
                {params.value}
              </Typography>
            </Stack>
          </LightTooltip>
        );
      },
    },
    {
      field: "value",
      headerName: title,
      minWidth: 160,
      resizable: false,
      align: "right",
      headerAlign: "right",
      renderHeader: () => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2" fontWeight="600">
            {title}
          </Typography>
          <LightTooltip
            title={
              <Typography variant="body2">
                {helpText.split(highlightText).map((part, index, array) => (
                  <React.Fragment key={index}>
                    {part}
                    {index < array.length - 1 && (
                      <strong>{highlightText}</strong>
                    )}
                  </React.Fragment>
                ))}
              </Typography>
            }
            placement="bottom"
          >
            <IconButton size="small" sx={{ p: 0 }}>
              <HelpOutlineIcon fontSize="small" color="action" />
            </IconButton>
          </LightTooltip>
        </Box>
      ),
      renderCell: (params) => {
        if (loading) {
          return <Skeleton variant="text" width="60%" />;
        }

        return (
          <Typography variant="body2" fontWeight="500">
            {params.value}
          </Typography>
        );
      },
    },
  ];

  // Create skeleton rows for loading state
  const skeletonRows = Array.from(
    { length: paginationModel.pageSize },
    (_, index) => ({
      id: `skeleton-${index}`,
      productName: "",
      value: 0,
      vendorUrl: "",
    })
  );

  const rows = loading
    ? skeletonRows
    : data.map((item, index) => ({
        id: index,
        ...item,
      }));

  const hasData = data.length > 0;
  const showEmptyState = !loading && !hasData;

  return (
    <>
      {/* Count display */}
      {loading ? (
        <Stack direction="row" alignItems="center" height={24}>
          <Skeleton
            variant="text"
            width={24}
            height={40}
            sx={{ display: "inline-block", mr: 1 }}
          />
          <Typography variant="h5" fontWeight={500}>
            {title} found
          </Typography>
        </Stack>
      ) : (
        <Typography variant="h5" fontWeight={500} height={24}>
          {`${formatNumber(count)} ${title} found`}
        </Typography>
      )}

      {showEmptyState ? (
        <EmptyState
          title="No data available"
          description={`No ${title.toLowerCase()} data found for this company at this time`}
          height={630}
        />
      ) : (
        <Box>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSizeOptions={[10, 25, 50, 100]}
            paginationMode="server"
            rowCount={count}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            sortingMode="server"
            disableColumnSorting
            disableColumnMenu
            disableColumnResize
            disableColumnSelector
            disableRowSelectionOnClick
            disableVirtualization
            slotProps={{
              pagination: {
                showFirstButton: true,
                showLastButton: true,
              },
            }}
            sx={{
              height: 630,
              "&, [class^=MuiDataGrid]": { border: "none" },
              ".MuiDataGrid-scrollbarFiller": {
                display: "none",
              },
              "& .MuiDataGrid-scrollbar--vertical": {
                ...lightScrollbarStyle,
              },
              [`& .${gridClasses.columnSeparator}`]: {
                [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
                  display: "none",
                },
              },
              "& .MuiDataGrid-filler": {
                display: "none",
              },

              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
                border: "none",
                paddingLeft: 0,
                "&:focus": {
                  outline: "none",
                },
                "&:focus-within": {
                  outline: "none",
                },
              },
              "& .MuiDataGrid-row": {
                borderBottom: "none",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
                "&:last-child .MuiDataGrid-cell": {
                  borderBottom: "none",
                },
              },
              "& .MuiDataGrid-footerContainer": {
                "& .MuiSelect-select": {
                  fontSize: 12,
                },
              },

              "& .MuiDataGrid-columnHeaders": {
                border: "none",
                borderBottom: "none",
              },
              "& .MuiDataGrid-columnHeader": {
                cursor: "default",
                borderBottom: "none !important",
                paddingLeft: 0,
                "&:focus": {
                  outline: "none",
                },
                "&:focus-within": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  "&:focus": {
                    outline: "none",
                  },
                },
              },
            }}
          />
        </Box>
      )}
    </>
  );
};
